<template>
  <div class="p-6 mx-auto print-container text-base" style="max-width: 1200px">
    <!-- Foto ampliada desde la miniatura de la lista de items -->
    <div
      v-if="photoPreview"
      ref="photoPreview"
      class="pm-photo-overlay no-print"
      tabindex="-1"
      @click.self="photoPreview = null"
      @keydown.esc="photoPreview = null"
    >
      <div class="pm-photo-dialog">
        <div class="pm-photo-header">
          <div class="pm-photo-title">
            <b>{{ photoPreview.sku }}</b>
            <span>{{ photoPreview.description }}</span>
          </div>
          <button
            type="button"
            class="pm-photo-close"
            @click="photoPreview = null"
          >
            <i class="mdi mdi-close"></i>
          </button>
        </div>
        <img
          :src="photoPreview.image"
          :alt="photoPreview.sku"
          class="pm-photo-img"
        />
      </div>
    </div>
    <div class="flex justify-between items-center pb-3 border-b no-print">
      <div class="flex items-center gap-3">
        <a class="cursor-pointer text-gray-500" @click="goBackToOrder">
          <i class="mdi mdi-arrow-left text-3xl"></i>
        </a>
        <h3 class="text-2xl font-semibold text-gray-900">
          {{ $t("message.order.packing_manifest") }} — #{{ orderNumbersLabel }}
        </h3>
      </div>
      <div class="flex items-center gap-3">
        <div class="flex gap-1" :title="$t('message.order.units')">
          <span
            class="pill"
            :class="{ active: !useMetric }"
            @click="setUnitSystem('imperial')"
            >LB · IN · FT³</span
          >
          <span
            class="pill"
            :class="{ active: useMetric }"
            @click="setUnitSystem('metric')"
            >KG · CM · M³</span
          >
        </div>
        <button v-if="view !== 'pack'" class="btn-primary-sm" @click="print">
          <i class="mdi mdi-printer"></i> {{ $t("message.order.print") }}
        </button>
      </div>
    </div>

    <div v-if="loading" class="py-10 text-center text-gray-500">
      {{ $t("message.order.loading") }}...
    </div>

    <div v-else-if="manifest">
      <div class="flex gap-2 mt-4 no-print">
        <button
          class="tab-btn"
          :class="{ active: view === 'pack' }"
          @click="view = 'pack'"
        >
          {{ $t("message.order.pack_items") }}
        </button>
        <button
          class="tab-btn"
          :class="{ active: view === 'summary' }"
          @click="view = 'summary'"
        >
          {{ $t("message.order.summary_and_print") }}
        </button>
        <button
          class="tab-btn"
          :class="{ active: view === 'receipt' }"
          @click="view = 'receipt'"
        >
          <i class="mdi mdi-file-sign"></i>
          {{ $t("message.order.receipt_tab") }}
        </button>
      </div>

      <!-- Órdenes que salen en estos mismos bultos: la principal y las agregadas. -->
      <div class="pm-orders no-print mt-4">
        <span class="text-sm uppercase text-gray-500">
          {{ $t("message.order.manifest_orders") }}
        </span>
        <span
          v-for="o in orders"
          :key="o.orderNumber"
          class="pm-order-chip"
          :class="{ principal: o.isPrincipal }"
        >
          <a class="font-mono cursor-pointer" @click="goToOrder(o.orderNumber)"
            >#{{ o.orderNumber }}</a
          >
          <small v-if="o.isPrincipal && isMerged">{{
            $t("message.order.principal_order")
          }}</small>
          <button
            v-else-if="!o.isPrincipal && manifest.status !== 'closed'"
            type="button"
            :title="$t('message.order.remove_order_from_manifest')"
            @click="unlinkOrder(o)"
          >
            <i class="mdi mdi-close"></i>
          </button>
        </span>
        <form
          v-if="manifest.status !== 'closed'"
          class="flex gap-2"
          @submit.prevent="linkOrder"
        >
          <input
            v-model.trim="linkOrderNumber"
            inputmode="numeric"
            class="border rounded px-2 py-1 w-36 font-mono"
            :placeholder="$t('message.order.order_number_placeholder')"
          />
          <button
            type="submit"
            class="btn-sm whitespace-nowrap"
            :disabled="linking || !linkOrderNumber"
          >
            <i class="mdi mdi-plus"></i>
            {{ $t("message.order.add_order_to_manifest") }}
          </button>
        </form>
      </div>
      <div v-if="shipToMismatch" class="banner-warn no-print mt-2">
        {{
          $t("message.order.ship_to_mismatch", {
            number: manifest.orderNumber
          })
        }}
      </div>

      <!-- PACK VIEW -->
      <div v-if="view === 'pack'" class="pack-progress mt-4">
        <div class="flex justify-between text-sm text-gray-600 mb-1">
          <span>
            {{
              $t("message.order.pack_progress", {
                packed: progress.packed,
                total: progress.total,
                bultos: closedBultos.length
              })
            }}
          </span>
          <b>{{ progress.pct }}%</b>
        </div>
        <div class="pack-progress-bar">
          <div :style="{ width: progress.pct + '%' }"></div>
        </div>
      </div>
      <div
        v-if="view === 'pack'"
        class="grid grid-cols-1 lg:grid-cols-5 gap-6 mt-4"
      >
        <div class="lg:col-span-3">
          <div class="flex justify-between items-center mb-2">
            <h4 class="font-semibold text-lg">
              {{ $t("message.order.items_to_pack") }} ({{ pendingItemCount }})
            </h4>
            <button
              v-if="packedItemCount"
              class="text-sm text-blue-500"
              @click="showPackedItems = !showPackedItems"
            >
              {{
                showPackedItems
                  ? $t("message.order.hide_packed_items")
                  : $t("message.order.show_packed_items", {
                      count: packedItemCount
                    })
              }}
            </button>
          </div>
          <div class="relative mb-2">
            <i
              class="mdi mdi-magnify absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            ></i>
            <input
              v-model="itemFilter"
              type="search"
              class="border rounded pl-9 pr-3 py-2 w-full"
              :placeholder="$t('message.order.filter_by_code')"
              @keydown.enter="addSingleFilteredItem"
            />
          </div>
          <p
            v-if="!visibleItems.length"
            class="text-sm text-gray-500 border rounded p-4 bg-gray-50"
          >
            {{
              itemFilter
                ? $t("message.order.no_items_match")
                : $t("message.order.all_items_packed")
            }}
          </p>
          <table v-else class="w-full text-base">
            <thead>
              <tr class="text-left text-gray-500 border-b">
                <th class="py-2 pr-4">SKU</th>
                <th class="text-sm font-normal">
                  {{ $t("message.order.description") }}
                </th>
                <th class="whitespace-nowrap">
                  {{ $t("message.order.remaining") }}
                </th>
                <th class="w-1"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in visibleItems"
                :key="item.id"
                class="border-b"
                :class="itemRowClass(item)"
              >
                <td class="py-2 pr-4 font-mono whitespace-nowrap">
                  <div class="flex items-center gap-2">
                    <button
                      v-if="item.image"
                      type="button"
                      class="pm-thumb"
                      :title="$t('message.order.view_photo')"
                      @click="openPhoto(item)"
                    >
                      <img
                        :src="item.image"
                        :alt="item.sku"
                        loading="lazy"
                        @error="setItemImage(item, null)"
                      />
                    </button>
                    <span
                      v-else-if="item.image === null"
                      class="pm-thumb pm-thumb-icon pm-thumb-none"
                      :title="$t('message.order.no_photo')"
                    >
                      <i class="mdi mdi-image-off-outline"></i>
                    </span>
                    <button
                      v-else
                      type="button"
                      class="pm-thumb pm-thumb-icon"
                      :title="$t('message.order.view_photo')"
                      @click="openPhoto(item)"
                    >
                      <i class="mdi mdi-camera-outline"></i>
                    </button>
                    {{ item.sku }}
                    <span v-if="isMerged" class="pm-order-tag"
                      >#{{ item.orderNumber }}</span
                    >
                  </div>
                </td>
                <!-- La descripción es secundaria: una línea, recortada; completa en el tooltip. -->
                <td class="w-full max-w-0 pr-3">
                  <div
                    class="truncate text-sm text-gray-500"
                    :title="item.description"
                  >
                    {{ item.description }}
                  </div>
                </td>
                <td class="font-mono whitespace-nowrap">
                  {{ item.remainingQty }}/{{ item.quantity }}
                  <span v-if="draftItemIds.has(item.id)" class="draft-badge">
                    {{ $t("message.order.in_current_draft") }}
                  </span>
                </td>
                <td class="text-right">
                  <button
                    v-if="item.remainingQty > 0"
                    class="btn-sm whitespace-nowrap"
                    @click="addItemToDraftBulto(item)"
                  >
                    <i class="mdi mdi-plus"></i>
                    {{ $t("message.order.add_bulto") }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="lg:col-span-2 pack-side">
          <div
            v-if="
              !editingBultoId &&
                !draftBulto.contents.length &&
                !hasRemainingItems
            "
            class="text-sm text-gray-500 border rounded p-4 bg-gray-50 mb-4"
          >
            {{ $t("message.order.all_items_packed") }}
          </div>
          <template v-else>
            <h4 class="font-semibold mb-2 text-lg">
              {{
                editingBultoId
                  ? `${$t("message.order.edit_bulto")} · ${editingBultoLabel}`
                  : $t("message.order.new_bulto")
              }}
            </h4>
            <div class="border rounded p-4 bg-gray-50">
              <label class="block text-sm uppercase text-gray-500 mb-2">{{
                $t("message.order.bulto_type")
              }}</label>
              <div class="flex gap-2 mb-4">
                <span
                  v-for="t in ['Caja', 'Pallet', 'Bulto']"
                  :key="t"
                  class="pill"
                  :class="{ active: draftBulto.type === t }"
                  @click="draftBulto.type = t"
                  >{{ t }}</span
                >
              </div>

              <div v-if="draftBulto.contents.length" class="mb-4">
                <label class="block text-sm uppercase text-gray-500 mb-1">
                  {{
                    $t("message.order.bulto_contents", {
                      lines: draftBulto.contents.length,
                      pcs: bultoTotalQty(draftBulto)
                    })
                  }}
                </label>
                <div class="draft-contents">
                  <div
                    v-for="(c, idx) in draftBulto.contents"
                    :key="c.itemId"
                    class="flex items-center gap-2 py-2 border-b border-dashed text-base"
                  >
                    <div class="flex-1 font-mono">
                      {{ c.sku }} — {{ c.description | truncate(30) }}
                    </div>
                    <input
                      type="number"
                      min="0"
                      :max="c.max"
                      class="w-20 border rounded px-2 py-1 font-mono"
                      v-model.number="c.qty"
                      @change="c.qty = clamp(c.qty, 0, c.max)"
                    />
                    <button
                      class="text-red-500 text-lg"
                      @click="removeContentLine(idx)"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              </div>
              <p v-else class="text-sm text-gray-500 mb-4">
                {{ $t("message.order.empty_bulto_hint") }}
              </p>

              <label class="block text-sm uppercase text-gray-500 mb-1">
                {{ $t("message.order.weight") }}
              </label>
              <div class="flex gap-2 mb-4">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  class="border rounded px-3 py-2 flex-1"
                  v-model.number="draftBulto.weightVal"
                  :placeholder="$t('message.order.weight')"
                />
                <span class="unit-tag">{{ draftBulto.weightUnit }}</span>
              </div>

              <label class="block text-sm uppercase text-gray-500 mb-1">
                {{ $t("message.order.bulto_dims") }}
              </label>
              <div class="flex gap-2 mb-4 items-center">
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  class="border rounded px-3 py-2 w-1/4"
                  v-model.number="draftBulto.l"
                  placeholder="L"
                />
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  class="border rounded px-3 py-2 w-1/4"
                  v-model.number="draftBulto.w"
                  placeholder="A"
                />
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  class="border rounded px-3 py-2 w-1/4"
                  v-model.number="draftBulto.h"
                  placeholder="H"
                />
                <span class="unit-tag">{{ draftBulto.dimUnit }}</span>
              </div>

              <p v-if="bultoError" class="text-red-500 text-sm mb-2">
                {{ bultoError }}
              </p>

              <div class="flex gap-2 items-center">
                <button class="btn-primary-sm" @click="saveBulto">
                  {{
                    editingBultoId
                      ? $t("message.order.save_changes")
                      : $t("message.order.close_bulto")
                  }}
                </button>
                <button
                  v-if="editingBultoId"
                  class="btn-sm"
                  @click="cancelEditBulto"
                >
                  {{ $t("message.order.cancel") }}
                </button>
                <button
                  v-if="!editingBultoId && draftBulto.id"
                  class="btn-sm"
                  @click="discardDraft"
                >
                  {{ $t("message.order.discard_draft") }}
                </button>
                <span
                  v-if="!editingBultoId && draftBulto.contents.length"
                  class="text-xs text-gray-400 ml-1"
                >
                  {{
                    draftSaving
                      ? $t("message.order.draft_saving")
                      : draftBulto.id
                      ? $t("message.order.draft_saved")
                      : ""
                  }}
                </span>
              </div>
            </div>
          </template>

          <h4 class="font-semibold mt-4 mb-2 text-lg">
            {{ $t("message.order.closed_bultos") }}
          </h4>
          <div v-if="!closedBultos.length" class="text-sm text-gray-500">
            {{ $t("message.order.no_bultos_yet") }}
          </div>
          <div
            v-for="(b, idx) in closedBultos"
            :key="b.id"
            class="border rounded p-3 mb-2 bg-white text-base"
            :class="{ 'bulto-editing': b.id === editingBultoId }"
          >
            <div class="flex justify-between items-center mb-1">
              <b>{{ b.type }} #{{ idx + 1 }}</b>
              <div class="flex gap-3">
                <button class="text-blue-500" @click="editBulto(b)">
                  {{ $t("message.order.edit") }}
                </button>
                <button class="text-red-500" @click="removeBulto(b)">
                  {{ $t("message.order.delete") }}
                </button>
              </div>
            </div>
            <div class="text-gray-600 text-sm">
              {{
                $t("message.order.bulto_summary", {
                  lines: b.contents.length,
                  pcs: bultoTotalQty(b)
                })
              }}
              · {{ bultoMeasuresText(b) }}
            </div>
            <details class="text-sm mt-1">
              <summary class="cursor-pointer text-gray-500">
                {{ $t("message.order.show_contents") }}
              </summary>
              <div class="text-gray-600 font-mono mt-1">
                {{ b.contents.map(c => `${c.sku} x${c.qty}`).join(", ") }}
              </div>
            </details>
          </div>
        </div>
      </div>

      <!-- DELIVERY RECEIPT: lo firma quien retira en el almacén. Sin montos. -->
      <div v-else-if="view === 'receipt'" class="mt-4 print-content">
        <div v-if="progress.pct < 100" class="banner-warn no-print">
          {{
            $t("message.order.incomplete_warning", {
              remaining: progress.total - progress.packed
            })
          }}
        </div>

        <div class="pl-sheet">
          <div class="pl-head">
            <div class="pl-company">
              <img :src="logoDataUri" alt="NKR" class="pl-logo" />
              <div>
                <div class="pl-company-name">
                  {{ companySettings.displayName }}
                </div>
                <div v-if="headerAddress" class="pl-company-line">
                  {{ headerAddress }}
                </div>
                <div v-if="headerPhone" class="pl-company-line">
                  {{ headerPhone }}
                </div>
                <div v-if="companySettings.url" class="pl-company-line">
                  {{ companySettings.url }}
                </div>
              </div>
            </div>
            <div class="pl-order-meta">
              <div>
                <b>{{ $t("message.order.order_no_label") }}</b>
                {{ orderNumbersLabel }}
              </div>
              <div>
                <b>{{ $t("message.order.receipt_order_date") }}</b>
                {{ formatDate(order_ && order_.orderDate) }}
              </div>
              <div>
                <b>{{ $t("message.order.receipt_printed_at") }}</b>
                {{ receiptPrintedAt() }}
              </div>
            </div>
          </div>

          <h1 class="pl-title">{{ $t("message.order.receipt_title") }}</h1>

          <div class="rc-info-row">
            <div class="pl-address-box">
              <div class="pl-address-label">
                {{ $t("message.order.receipt_recipient") }}
              </div>
              <template v-if="hasForwarder">
                <div
                  v-for="(line, i) in addressLines(order_.forwarderAddress)"
                  :key="'f' + i"
                  :class="{ 'font-bold': i === 0 }"
                >
                  {{ line }}
                </div>
              </template>
              <div v-else class="text-gray-600">
                {{ $t("message.order.receipt_same_as_destination") }}
              </div>
            </div>
            <div class="pl-address-box">
              <div class="pl-address-label">
                {{ $t("message.order.receipt_final_destination") }}
              </div>
              <div
                v-for="(line, i) in addressLines(
                  order_ && order_.shippingAddress
                )"
                :key="'s' + i"
                :class="{ 'font-bold': i === 0 }"
              >
                {{ line }}
              </div>
            </div>
            <div class="pl-address-box">
              <div class="pl-address-label">
                {{ $t("message.order.receipt_general_info") }}
              </div>
              <div v-for="row in receiptInfoRows" :key="row.label">
                <b>{{ row.label }}:</b> {{ row.value }}
              </div>
            </div>
          </div>

          <table class="pl-table rc-table">
            <colgroup>
              <col style="width: 8%" />
              <col style="width: 32%" />
              <col style="width: 24%" />
              <col style="width: 20%" />
              <col style="width: 16%" />
            </colgroup>
            <thead>
              <tr>
                <th>#</th>
                <th>{{ $t("message.order.receipt_package") }}</th>
                <th>{{ sizeColumnLabel }}</th>
                <th>{{ weightColumnLabel }}</th>
                <th>{{ $t("message.order.quantity") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(b, idx) in closedBultos" :key="b.id">
                <td>{{ idx + 1 }}</td>
                <td>{{ packageLabel(b, idx) }}</td>
                <td>{{ sizeLabel(b) }} {{ sizeUnitLabel }}</td>
                <td>{{ weightDisplay(b).toFixed(2) }} {{ weightUnitLabel }}</td>
                <td>{{ bultoTotalQty(b) }}</td>
              </tr>
              <tr v-if="!closedBultos.length">
                <td colspan="5" class="text-center text-gray-500">
                  {{ $t("message.order.no_bultos_yet") }}
                </td>
              </tr>
            </tbody>
          </table>

          <table class="pl-totals-table">
            <tbody>
              <tr>
                <td class="pl-tot-label">
                  {{ $t("message.order.package_count_label") }}
                </td>
                <td class="pl-tot-value">
                  {{ closedBultos.length }}
                  <span v-if="packageSummary">({{ packageSummary }})</span>
                </td>
                <td class="pl-tot-label">{{ weightTotalLabel }}</td>
                <td class="pl-tot-value">
                  {{ totalWeightDisplay.toFixed(2) }} {{ weightUnitLabel }}
                </td>
              </tr>
              <tr>
                <td class="pl-tot-label">
                  {{ $t("message.order.receipt_total_items") }}
                </td>
                <td class="pl-tot-value">{{ totalPackedQty }}</td>
                <td class="pl-tot-label">{{ volumeTotalLabel }}</td>
                <td class="pl-tot-value">
                  {{ totalVolumeDisplay.toFixed(3) }} {{ volumeUnitLabel }}
                </td>
              </tr>
            </tbody>
          </table>

          <p class="rc-ack">
            {{ $t("message.order.receipt_acknowledgement") }}
          </p>

          <div class="rc-signs">
            <div>
              <div class="rc-sign-title">
                {{ $t("message.order.receipt_dispatched_by") }}
              </div>
              <div class="rc-sign-line">
                <span>{{ dispatch.shippedBy }}</span>
                <label>{{ $t("message.order.receipt_name") }}</label>
              </div>
              <div class="rc-sign-line">
                <span></span>
                <label>{{ $t("message.order.receipt_signature") }}</label>
              </div>
            </div>
            <div>
              <div class="rc-sign-title">
                {{ $t("message.order.receipt_received_by") }}
              </div>
              <div class="rc-sign-line">
                <span></span>
                <label>{{ $t("message.order.receipt_name") }}</label>
              </div>
              <div class="rc-sign-grid">
                <div class="rc-sign-line">
                  <span></span>
                  <label>{{ $t("message.order.receipt_id_document") }}</label>
                </div>
                <div class="rc-sign-line">
                  <span></span>
                  <label>{{ $t("message.order.receipt_date_time") }}</label>
                </div>
              </div>
              <div class="rc-sign-line">
                <span></span>
                <label>{{ $t("message.order.receipt_signature") }}</label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- SUMMARY / PRINT VIEW -->
      <div v-else class="mt-4 print-content">
        <div v-if="progress.pct < 100" class="banner-warn no-print">
          {{
            $t("message.order.incomplete_warning", {
              remaining: progress.total - progress.packed
            })
          }}
        </div>

        <div class="pl-sheet">
          <div class="pl-head">
            <div class="pl-company">
              <img :src="logoDataUri" alt="NKR" class="pl-logo" />
              <div>
                <div class="pl-company-name">
                  {{ companySettings.displayName }}
                </div>
                <div v-if="headerAddress" class="pl-company-line">
                  {{ headerAddress }}
                </div>
                <div v-if="headerPhone" class="pl-company-line">
                  {{ headerPhone }}
                </div>
                <div v-if="companySettings.url" class="pl-company-line">
                  {{ companySettings.url }}
                </div>
              </div>
            </div>
            <div class="pl-order-meta">
              <div>
                <b>{{ $t("message.order.order_no_label") }}</b>
                {{ orderNumbersLabel }}
              </div>
              <div>
                <b>{{ $t("message.order.order_date_label") }}</b>
                {{ formatDate(order_ && order_.orderDate) }}
              </div>
              <div>
                <b>{{ $t("message.order.pick_date_label") }}</b>
                {{ formatDate(manifest.closedAt) }}
              </div>
            </div>
          </div>

          <h1 class="pl-title">{{ $t("message.order.packing_manifest") }}</h1>

          <div v-if="progress.pct < 100" class="banner-warn no-print mb-3">
            {{
              $t("message.order.incomplete_warning", {
                remaining: progress.total - progress.packed
              })
            }}
          </div>

          <div class="pl-address-row">
            <div class="pl-address-box">
              <div class="pl-address-label">
                {{ $t("message.order.billed_to") }}
              </div>
              <div>{{ formattedAddress("billingAddress") }}</div>
            </div>
            <div class="pl-address-box">
              <div class="pl-address-label">
                {{ $t("message.order.ship_to") }}
              </div>
              <div>{{ formattedAddress("shippingAddress") }}</div>
            </div>
          </div>

          <table class="pl-table">
            <colgroup>
              <col style="width: 11%" />
              <col style="width: 11%" />
              <col style="width: 28%" />
              <col style="width: 7%" />
              <col style="width: 6%" />
              <col style="width: 13%" />
              <col style="width: 12%" />
              <col style="width: 12%" />
            </colgroup>
            <thead>
              <tr>
                <th>{{ $t("message.order.package_type") }}</th>
                <th>{{ $t("message.order.part_numbers") }}</th>
                <th>{{ $t("message.order.descriptions") }}</th>
                <th>{{ $t("message.order.quantity") }}</th>
                <th>{{ $t("message.order.unit") }}</th>
                <th>{{ sizeColumnLabel }}</th>
                <th>{{ volumeColumnLabel }}</th>
                <th>{{ weightColumnLabel }}</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(b, idx) in closedBultos">
                <tr v-for="(c, ci) in b.contents" :key="b.id + '-' + ci">
                  <td>{{ ci === 0 ? packageLabel(b, idx) : "" }}</td>
                  <td>
                    {{ c.sku }}
                    <div v-if="isMerged" class="pl-order-ref">
                      #{{ itemOrderNumber[c.itemId] }}
                    </div>
                  </td>
                  <td>{{ c.description }}</td>
                  <td>{{ c.qty }}</td>
                  <td>{{ c.unit }}</td>
                  <td></td>
                  <td></td>
                  <td></td>
                </tr>
                <tr :key="b.id + '-subtotal'" class="pl-subtotal-row">
                  <td colspan="3">
                    <i>{{ packageLabel(b, idx) }}</i>
                  </td>
                  <td>{{ bultoTotalQty(b) }}</td>
                  <td></td>
                  <td>{{ sizeLabel(b) }} {{ sizeUnitLabel }}</td>
                  <td>
                    {{ volumeDisplay(b).toFixed(3) }} {{ volumeUnitLabel }}
                  </td>
                  <td>
                    {{ weightDisplay(b).toFixed(3) }} {{ weightUnitLabel }}
                  </td>
                </tr>
              </template>
              <tr v-if="!closedBultos.length">
                <td colspan="8" class="text-center text-gray-500">
                  {{ $t("message.order.no_bultos_yet") }}
                </td>
              </tr>
            </tbody>
          </table>

          <table class="pl-totals-table">
            <tbody>
              <tr>
                <td class="pl-tot-label">
                  {{ $t("message.order.total_label") }}
                </td>
                <td class="pl-tot-value">{{ closedBultos.length }}</td>
                <td class="pl-tot-label">{{ weightTotalLabel }}</td>
                <td class="pl-tot-value">
                  {{ totalWeightDisplay.toFixed(2) }} {{ weightUnitLabel }}
                </td>
              </tr>
              <tr>
                <td class="pl-tot-label">
                  {{ $t("message.order.package_count_label") }}
                </td>
                <td class="pl-tot-value">{{ packageSummary }}</td>
                <td class="pl-tot-label">{{ volumeTotalLabel }}</td>
                <td class="pl-tot-value">
                  {{ totalVolumeDisplay.toFixed(3) }} {{ volumeUnitLabel }}
                </td>
              </tr>
            </tbody>
          </table>

          <div class="pl-signlines">
            <div class="pl-sl">
              <label>{{ $t("message.order.picked_by") }}</label>
              <input
                v-model="dispatch.pickedBy"
                @input="saveDispatchDebounced"
              />
            </div>
            <div class="pl-sl">
              <label>{{ $t("message.order.revised_by") }}</label>
              <input
                v-model="dispatch.revisedBy"
                @input="saveDispatchDebounced"
              />
            </div>
            <div class="pl-sl">
              <label>{{ $t("message.order.shipped_by") }}</label>
              <input
                v-model="dispatch.shippedBy"
                @input="saveDispatchDebounced"
              />
            </div>
          </div>
        </div>

        <div class="btn-row no-print mt-4">
          <button
            v-if="manifest.status !== 'closed'"
            class="btn-primary-sm"
            @click="handleClose"
          >
            {{ $t("message.order.mark_as_dispatched") }}
          </button>
          <button v-else class="btn-sm" @click="handleReopen">
            {{ $t("message.order.reopen_manifest") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getPackingManifest,
  createManifestBulto,
  updateManifestBulto,
  deleteManifestBulto,
  updateManifestDispatchInfo,
  closeManifest,
  reopenManifest,
  linkManifestOrder,
  unlinkManifestOrder
} from "@/api/store/PackingManifestService.js";
import { getSettings } from "@/api/store/SettingsService.js";
import { getProductImages } from "@/api/store/ProductsService.js";
import logoUrl from "@/assets/img/logo.png";

// Para comparar destinos sin que un espacio o una mayúscula los haga distintos.
function addressKey(a) {
  return [a && a.street1, a && a.postalCode, a && a.country]
    .map(normalizeCode)
    .join("|");
}

// "950-1043", "9501043" y "950 1043" son el mismo código.
function normalizeCode(code) {
  return String(code || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function round2(n) {
  return Math.round(n * 100) / 100;
}

const UNIT_SYSTEM_KEY = "packingManifest.unitSystem";

// Preferencia del usuario (imperial/metric); null = automático según los bultos.
function storedUnitSystem() {
  try {
    const v = localStorage.getItem(UNIT_SYSTEM_KEY);
    return v === "metric" || v === "imperial" ? v : null;
  } catch (e) {
    return null;
  }
}

function newDraftBulto(metric = storedUnitSystem() === "metric") {
  return {
    id: null,
    type: "Caja",
    contents: [],
    weightVal: "",
    weightUnit: metric ? "kg" : "lb",
    l: "",
    w: "",
    h: "",
    dimUnit: metric ? "cm" : "in"
  };
}

export default {
  name: "PackingManifest",
  props: {
    orderNumber: { type: [String, Number], required: true }
  },
  filters: {
    truncate(value, length) {
      if (!value) return "";
      return value.length > length ? value.slice(0, length) + "…" : value;
    }
  },
  data() {
    return {
      loading: true,
      view: "pack",
      unitSystem: storedUnitSystem(),
      showPackedItems: false,
      itemFilter: "",
      photoPreview: null,
      manifest: null,
      order_: null,
      orders: [],
      linkOrderNumber: "",
      linking: false,
      items: [],
      bultos: [],
      draftBulto: newDraftBulto(),
      editingBultoId: null,
      bultoError: "",
      draftSaving: false,
      draftSaveTimer: null,
      autosaveSeq: 0,
      suppressAutosave: false,
      dispatch: { pickedBy: "", revisedBy: "", shippedBy: "" },
      dispatchSaveTimer: null,
      // Encabezado del packing list impreso: viene de Settings (admin > general settings),
      // no hardcodeado, para no tener que tocar código si cambia la razón social/dirección.
      companySettings: {
        displayName: "",
        address: "",
        phone: "",
        url: ""
      }
    };
  },
  computed: {
    // Más de una orden en los mismos bultos.
    isMerged() {
      return this.orders.length > 1;
    },
    orderNumbersLabel() {
      return this.orders.length
        ? this.orders.map(o => o.orderNumber).join(" + ")
        : this.orderNumber;
    },
    // De qué orden es cada línea de un bulto (itemId -> número de orden).
    itemOrderNumber() {
      const map = {};
      this.items.forEach(i => {
        map[i.id] = i.orderNumber;
      });
      return map;
    },
    shipToMismatch() {
      return (
        this.isMerged &&
        new Set(this.orders.map(o => addressKey(o.shippingAddress))).size > 1
      );
    },
    // El encabezado impreso muestra la direccion/telefono del departamento que
    // despacha la orden; si el departamento no los tiene cargados, cae a los
    // datos globales de la tienda (admin > general settings).
    headerAddress() {
      return (
        (this.order_ &&
          this.order_.department &&
          this.order_.department.direccion) ||
        this.companySettings.address
      );
    },
    headerPhone() {
      return (
        (this.order_ &&
          this.order_.department &&
          this.order_.department.telefono) ||
        this.companySettings.phone
      );
    },
    closedBultos() {
      // Tolerante a bultos sin campo "status" (backend sin la migración de estados
      // aplicada todavía): todo lo que no sea explícitamente "open" se trata como cerrado.
      return this.bultos.filter(b => b.status !== "open");
    },
    // Disponibilidad que ve el usuario: la del servidor, corregida con lo que el bulto en
    // pantalla tiene sin guardar. Al editar un bulto cerrado el servidor sigue contando su
    // contenido original hasta "Guardar cambios"; sin esto, quitar un item no lo devolvía a
    // la lista ni bajaba el porcentaje.
    packItems() {
      const d = this.draftBulto;
      const baseId = this.editingBultoId || d.id;
      const stored = baseId && this.bultos.find(b => b.id === baseId);
      const delta = {};
      (stored ? stored.contents : []).forEach(c => {
        delta[c.itemId] = (delta[c.itemId] || 0) + (+c.qty || 0);
      });
      d.contents.forEach(c => {
        delta[c.itemId] = (delta[c.itemId] || 0) - (+c.qty || 0);
      });
      return this.items.map(i =>
        delta[i.id]
          ? { ...i, remainingQty: Math.max(0, i.remainingQty + delta[i.id]) }
          : i
      );
    },
    hasRemainingItems() {
      return this.packItems.some(i => i.remainingQty > 0);
    },
    // Con texto en el filtro se busca en todos los items (también los ya empacados),
    // para poder ubicar un código aunque ya esté en un bulto.
    visibleItems() {
      const term = normalizeCode(this.itemFilter);
      if (term) {
        return this.packItems.filter(i => normalizeCode(i.sku).includes(term));
      }
      return this.showPackedItems
        ? this.packItems
        : this.packItems.filter(i => i.remainingQty > 0);
    },
    pendingItemCount() {
      return this.packItems.filter(i => i.remainingQty > 0).length;
    },
    packedItemCount() {
      return this.packItems.length - this.pendingItemCount;
    },
    editingBultoLabel() {
      const idx = this.closedBultos.findIndex(
        b => b.id === this.editingBultoId
      );
      return idx >= 0 ? `${this.closedBultos[idx].type} #${idx + 1}` : "";
    },
    draftItemIds() {
      return new Set(this.draftBulto.contents.map(c => c.itemId));
    },
    // Todo lo que debe disparar un (re)autoguardado del borrador, sin incluir "id" (ver watch).
    draftSignature() {
      const d = this.draftBulto;
      return JSON.stringify({
        type: d.type,
        contents: d.contents.map(c => ({ itemId: c.itemId, qty: c.qty })),
        weightVal: d.weightVal,
        weightUnit: d.weightUnit,
        l: d.l,
        w: d.w,
        h: d.h,
        dimUnit: d.dimUnit
      });
    },
    progress() {
      const total = this.packItems.reduce((s, i) => s + (+i.quantity || 0), 0);
      const packed = this.packItems.reduce(
        (s, i) => s + ((+i.quantity || 0) - (+i.remainingQty || 0)),
        0
      );
      return {
        total,
        packed,
        pct: total > 0 ? Math.round((packed / total) * 100) : 0
      };
    },
    totalWeightKg() {
      return this.closedBultos.reduce((s, b) => s + (+b.weightKg || 0), 0);
    },
    totalVolumeM3() {
      return this.closedBultos.reduce((s, b) => s + (+b.volumeM3 || 0), 0);
    },
    // Manda la unidad elegida en el selector. Sin elección, el packing list oficial usa
    // lb/ft³, salvo que TODOS los bultos se hayan cargado en kg (y por lo tanto cm).
    useMetric() {
      if (this.unitSystem) return this.unitSystem === "metric";
      return (
        this.closedBultos.length > 0 &&
        this.closedBultos.every(b => b.weightDisplayUnit === "kg")
      );
    },
    weightColumnLabel() {
      return this.useMetric
        ? this.$t("message.order.gross_weight_kg")
        : this.$t("message.order.gross_weight_lbs");
    },
    volumeColumnLabel() {
      return this.useMetric
        ? this.$t("message.order.volume_m3")
        : this.$t("message.order.volume_cft");
    },
    sizeColumnLabel() {
      return this.useMetric
        ? this.$t("message.order.size_lwh_cm")
        : this.$t("message.order.size_lwh");
    },
    weightUnitLabel() {
      return this.useMetric ? "kg" : "lb";
    },
    volumeUnitLabel() {
      return this.useMetric ? "m³" : "ft³";
    },
    sizeUnitLabel() {
      return this.useMetric ? "cm" : "in";
    },
    weightTotalLabel() {
      return this.useMetric
        ? this.$t("message.order.weight_kg_label")
        : this.$t("message.order.weight_lbs_label");
    },
    volumeTotalLabel() {
      return this.useMetric
        ? this.$t("message.order.volume_m3_label")
        : this.$t("message.order.volume_ft3_label");
    },
    totalWeightDisplay() {
      return this.useMetric
        ? this.totalWeightKg
        : this.totalWeightKg / 0.453592;
    },
    totalVolumeDisplay() {
      return this.useMetric
        ? this.totalVolumeM3
        : this.totalVolumeM3 * 35.3146667;
    },
    // "1 PALLET + 1 CARTON", igual al resumen del PDF de referencia.
    packageSummary() {
      const counts = {};
      this.closedBultos.forEach(b => {
        const label = this.packageTypeLabel(b.type);
        counts[label] = (counts[label] || 0) + 1;
      });
      return Object.entries(counts)
        .map(([label, count]) => `${count} ${label}${count > 1 ? "S" : ""}`)
        .join(" + ");
    },
    logoDataUri() {
      return logoUrl;
    },
    totalPackedQty() {
      return this.closedBultos.reduce((s, b) => s + this.bultoTotalQty(b), 0);
    },
    // Si la orden tiene forwarder, se le entrega a él y el shipping es el destino
    // final; si no, quien retira lleva la mercancía directo al shipping address.
    hasForwarder() {
      const f = this.order_ && this.order_.forwarderAddress;
      return !!(f && (f.name || f.company || f.street1));
    },
    receiptInfoRows() {
      const o = this.order_ || {};
      const carrier = [o.shippingCarrier, o.shippingService]
        .filter(Boolean)
        .join(" / ");
      const tracking = (o.shipments || [])
        .map(s => s.tracking)
        .filter(Boolean)
        .join(", ");
      // Con varias órdenes, cada una puede traer su PO.
      const purchaseOrders = this.isMerged
        ? [...new Set(this.orders.map(x => x.purchaseOrderNumber))]
            .filter(Boolean)
            .join(", ")
        : o.purchaseOrderNumber;
      return [
        {
          label: this.$t("message.order.receipt_customer_email"),
          value: o.customerEmail
        },
        { label: this.$t("message.order.receipt_seller"), value: o.seller },
        {
          label: this.$t("message.order.receipt_po"),
          value: purchaseOrders
        },
        {
          label: this.$t("message.order.receipt_carrier"),
          value: carrier || o.shippingPreference
        },
        { label: this.$t("message.order.receipt_tracking"), value: tracking }
      ].filter(row => row.value);
    }
  },
  watch: {
    // Se observa la "firma" del contenido en vez de draftBulto completo (deep) a propósito:
    // draftBulto.id se actualiza como resultado del propio autoguardado (al crear el bulto),
    // y un watch deep sobre todo el objeto disparaba un segundo guardado redundante solo por
    // ese cambio de id. La firma excluye el id, así solo cambios reales del contenido reagendan.
    draftSignature() {
      this.scheduleAutosave();
    },
    // El selector de arriba es la única unidad del manifiesto: todos los bultos se cargan
    // y se muestran en ella. Si cambia con un bulto a medio llenar, se convierten sus
    // valores para que sigan diciendo lo mismo.
    useMetric: {
      immediate: true,
      handler(metric) {
        const d = this.draftBulto;
        const weightUnit = metric ? "kg" : "lb";
        const dimUnit = metric ? "cm" : "in";
        if (d.weightUnit !== weightUnit) {
          if (+d.weightVal > 0) {
            const kg =
              d.weightUnit === "kg" ? +d.weightVal : d.weightVal * 0.453592;
            d.weightVal = round2(metric ? kg : kg / 0.453592);
          }
          d.weightUnit = weightUnit;
        }
        if (d.dimUnit !== dimUnit) {
          ["l", "w", "h"].forEach(k => {
            if (!(+d[k] > 0)) return;
            const cm = d.dimUnit === "cm" ? +d[k] : d[k] * 2.54;
            d[k] = this.cmToDimUnit(cm, dimUnit);
          });
          d.dimUnit = dimUnit;
        }
      }
    }
  },
  created() {
    this.load();
    this.loadCompanySettings();
  },
  methods: {
    async loadCompanySettings() {
      const [displayName, address, phone, url] = await Promise.all([
        getSettings("store_display_name"),
        getSettings("address"),
        getSettings("phone_number"),
        getSettings("store_url")
      ]);
      this.companySettings = {
        displayName: displayName?.value || "NKR DIESEL PARTS, INC",
        address: address?.value || "",
        phone: phone?.value || "",
        url: url?.value || ""
      };
    },
    formattedAddress(field) {
      const a = (this.order_ && this.order_[field]) || {};
      return [
        a.name,
        a.company,
        a.street1,
        a.street2,
        a.city,
        a.state,
        a.country
      ]
        .filter(Boolean)
        .join(", ");
    },
    // Dirección en renglones para el recibo, con teléfono. En CR la localidad viene en
    // barrio/distrito/cantón/provincia y a veces se repite en city/state: se deduplica.
    addressLines(a) {
      if (!a) return [];
      const seen = new Set();
      const locality = [
        a.barrio,
        a.distrito,
        a.canton,
        a.provincia,
        a.city,
        a.state,
        a.postalCode
      ].filter(part => {
        const key = String(part || "")
          .trim()
          .toLowerCase();
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      let phone = "";
      if (a.phone) {
        const area = String(a.phoneAreaCode || "").replace(/^\+/, "");
        phone = `Tel: ${area ? `+${area} ` : ""}${a.phone}`;
      }
      return [
        [a.name, a.company].filter(Boolean).join(" — "),
        a.street1,
        a.street2,
        a.street3,
        locality.join(", "),
        a.country,
        phone
      ].filter(Boolean);
    },
    // Método y no computed: un computed sin dependencias reactivas congelaría la hora.
    receiptPrintedAt() {
      const d = new Date();
      const pad = n => String(n).padStart(2, "0");
      return `${d.getMonth() + 1}/${d.getDate()}/${String(
        d.getFullYear()
      ).slice(-2)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    formatDate(value) {
      if (!value) return "—";
      const d = new Date(value.replace(" ", "T"));
      if (isNaN(d.getTime())) return "—";
      return `${d.getMonth() + 1}/${d.getDate()}/${String(
        d.getFullYear()
      ).slice(-2)}`;
    },
    // "Caja" -> CARTON, "Pallet" -> PALLET, "Bulto" -> PACKAGE: nombres tal cual el
    // packing list oficial (PALLET / CARTON) para los tipos más comunes.
    packageTypeLabel(type) {
      const map = { Caja: "CARTON", Pallet: "PALLET", Bulto: "PACKAGE" };
      return map[type] || (type || "").toUpperCase();
    },
    packageLabel(bulto, idx) {
      const sameTypeBefore = this.closedBultos
        .slice(0, idx)
        .filter(b => b.type === bulto.type).length;
      return `${this.packageTypeLabel(bulto.type)} ${sameTypeBefore + 1}`;
    },
    bultoTotalQty(bulto) {
      return bulto.contents.reduce((s, c) => s + (+c.qty || 0), 0);
    },
    sizeLabel(bulto) {
      if (this.useMetric) {
        const cm = v => Math.round(+v || 0);
        return `${cm(bulto.lengthCm)}x${cm(bulto.widthCm)}x${cm(
          bulto.heightCm
        )}`;
      }
      const toIn = cm => Math.round((+cm || 0) / 2.54);
      return `${toIn(bulto.lengthCm)}x${toIn(bulto.widthCm)}x${toIn(
        bulto.heightCm
      )}`;
    },
    volumeDisplay(bulto) {
      const m3 = +bulto.volumeM3 || 0;
      return this.useMetric ? m3 : m3 * 35.3146667;
    },
    weightDisplay(bulto) {
      const kg = +bulto.weightKg || 0;
      return this.useMetric ? kg : kg / 0.453592;
    },
    goBackToOrder() {
      this.$router.push({
        name: "OrderPage",
        params: { orderNumber: this.orderNumber }
      });
    },
    goToOrder(orderNumber) {
      this.$router.push({ name: "OrderPage", params: { orderNumber } });
    },
    print() {
      window.print();
    },
    clamp(v, min, max) {
      v = isNaN(v) ? min : v;
      return Math.min(Math.max(v, min), max);
    },
    // Distingue un item ya reservado en el bulto que se está armando (todavía editable,
    // "en progreso") de uno que ya quedó dentro de un bulto cerrado (definitivo).
    itemRowClass(item) {
      if (this.draftItemIds.has(item.id)) return "text-amber-700 font-medium";
      return item.remainingQty > 0 ? "text-gray-900" : "text-gray-400";
    },
    async load() {
      this.loading = true;
      try {
        const res = await getPackingManifest(this.orderNumber);
        this.applyResponse(res);
        await this.hydrateOpenDraft(res);
      } catch (e) {
        this.$toast.open({
          message:
            (e.response && e.response.data && e.response.data.message) ||
            "Error cargando el packing list",
          type: "error",
          position: "top-right"
        });
      } finally {
        this.loading = false;
      }
    },
    applyResponse(res) {
      this.manifest = res.manifest;
      this.order_ = res.order;
      this.orders = res.orders || [];
      this.items = res.items;
      this.bultos = res.bultos;
      this.dispatch = {
        pickedBy: res.manifest.pickedBy || "",
        revisedBy: res.manifest.revisedBy || "",
        shippedBy: res.manifest.shippedBy || ""
      };
    },
    // Al entrar/recargar la página, si ya había un bulto en progreso (no cerrado) guardado
    // como borrador, lo recupera para seguir armándolo sin perder lo avanzado.
    async hydrateOpenDraft(res) {
      const openBultos = (res.bultos || []).filter(b => b.status === "open");
      if (!openBultos.length) return;
      // Cargar el borrador ya guardado no debe disparar un autoguardado: es la misma data
      // que acaba de llegar del servidor, reenviarla sería una llamada redundante.
      this.suppressAutosave = true;

      let needsSave = false;
      if (openBultos.length === 1) {
        this.draftBulto = this.bultoToDraft(openBultos[0]);
      } else {
        // Estado corrupto de datos previos a esta corrección: no debería poder existir más
        // de un bulto 'open' por manifiesto. Se fusiona todo en el más antiguo localmente...
        const [primary, ...extras] = [...openBultos].sort(
          (a, b) => a.id - b.id
        );
        const merged = this.bultoToDraft(primary);
        extras.forEach(b => {
          b.contents.forEach(c => {
            if (merged.contents.find(mc => mc.itemId === c.itemId)) return;
            const item = this.items.find(i => i.id === c.itemId);
            const max = item ? item.remainingQty + c.qty : c.qty;
            merged.contents.push({ ...c, max });
          });
        });
        this.draftBulto = merged;
        // ...y hay que borrar los sobrantes en el servidor ANTES de guardar el borrador
        // fusionado: mientras existan, cada uno sigue reservando su cantidad y la validación
        // de remanentes rechazaría el merge por "duplicar" esa reserva.
        await this.cleanupExtraOpenBultos(extras.map(b => b.id));
        needsSave = true;
      }

      this.$nextTick(() => {
        this.suppressAutosave = false;
        // El watch de draftSignature ya "vio" el cambio del merge mientras suppressAutosave
        // seguía activo y no vuelve a dispararse solo; hay que agendar el guardado explícito.
        if (needsSave) this.scheduleAutosave();
      });
    },
    bultoToDraft(bulto) {
      const dimUnit = this.useMetric ? "cm" : "in";
      const weightUnit = this.useMetric ? "kg" : "lb";
      const kg = +bulto.weightKg || 0;
      // Si el bulto se pesó en la unidad actual se usa el valor tal cual se escribió;
      // si no, se convierte desde kg.
      const weightVal =
        bulto.weightDisplayUnit === weightUnit
          ? Number(bulto.weightDisplayVal)
          : round2(this.useMetric ? kg : kg / 0.453592);
      return {
        id: bulto.id,
        type: bulto.type,
        contents: bulto.contents.map(c => {
          const item = this.items.find(i => i.id === c.itemId);
          const max = item ? item.remainingQty + c.qty : c.qty;
          return { ...c, max };
        }),
        weightVal: weightVal || "",
        weightUnit,
        l: this.cmToDimUnit(bulto.lengthCm, dimUnit),
        w: this.cmToDimUnit(bulto.widthCm, dimUnit),
        h: this.cmToDimUnit(bulto.heightCm, dimUnit),
        dimUnit
      };
    },
    // El bulto guarda las medidas en cm; el formulario las muestra en la unidad en que se
    // cargaron. Sin esta conversión, cada edición volvía a multiplicar por 2.54.
    cmToDimUnit(cm, unit) {
      if (!(+cm > 0)) return "";
      return round2(unit === "in" ? +cm / 2.54 : +cm);
    },
    bultoMeasuresText(bulto) {
      const unit = this.useMetric ? "cm" : "in";
      const dims = [bulto.lengthCm, bulto.widthCm, bulto.heightCm]
        .map(cm => this.cmToDimUnit(cm, unit) || 0)
        .join("×");
      return [
        `${this.weightDisplay(bulto).toFixed(2)} ${this.weightUnitLabel}`,
        `${dims} ${unit}`,
        `${this.volumeDisplay(bulto).toFixed(3)} ${this.volumeUnitLabel}`
      ].join(" · ");
    },
    // Un API que todavía no manda "image" en los items deja la foto sin resolver
    // (undefined): se busca al hacer clic. null = el producto no tiene foto.
    async openPhoto(item) {
      let image = item.image;
      if (image === undefined) {
        try {
          const images = (await getProductImages(item.sku)) || [];
          const main = images.find(img => +img.main === 1) || images[0];
          image = (main && main.imageRoute) || null;
        } catch (e) {
          image = null;
        }
        this.setItemImage(item, image);
      }
      if (!image) {
        this.$toast.open({
          message: this.$t("message.order.no_photo"),
          type: "info",
          position: "top-right"
        });
        return;
      }
      this.photoPreview = { ...item, image };
      // Foco en el overlay para que Esc lo cierre.
      this.$nextTick(() => this.$refs.photoPreview?.focus());
    },
    // packItems son copias: el dato se guarda en el item original.
    setItemImage(item, image) {
      const original = this.items.find(i => i.id === item.id);
      if (original) this.$set(original, "image", image);
    },
    // Enter en el filtro (o un lector de código de barras) agrega el item si hay uno solo
    // pendiente que coincida, y limpia el filtro para el siguiente.
    addSingleFilteredItem() {
      const pending = this.visibleItems.filter(i => i.remainingQty > 0);
      if (pending.length !== 1) return;
      this.addItemToDraftBulto(pending[0]);
      this.itemFilter = "";
    },
    setUnitSystem(system) {
      this.unitSystem = system;
      try {
        localStorage.setItem(UNIT_SYSTEM_KEY, system);
      } catch (e) {
        // Sin storage la elección sólo dura esta visita.
      }
    },
    addItemToDraftBulto(item) {
      if (this.draftBulto.contents.find(c => c.itemId === item.id)) return;
      this.draftBulto.contents.push({
        itemId: item.id,
        sku: item.sku,
        description: item.description,
        unit: "PCS",
        qty: item.remainingQty,
        max: item.remainingQty
      });
    },
    // Quitar un item del borrador debe liberarlo de inmediato (tanto en la UI como en el
    // servidor), sin esperar el debounce del autoguardado normal.
    removeContentLine(idx) {
      this.draftBulto.contents.splice(idx, 1);
      // Un bulto cerrado en edición se guarda sólo con "Guardar cambios".
      if (this.editingBultoId) return;
      clearTimeout(this.draftSaveTimer);
      this.autosaveDraft();
    },
    editBulto(bulto) {
      this.editingBultoId = bulto.id;
      this.draftBulto = this.bultoToDraft(bulto);
      this.draftBulto.id = null; // el bulto editado ya está cerrado; no forma parte del autoguardado de borrador
      this.bultoError = "";
    },
    cancelEditBulto() {
      this.editingBultoId = null;
      this.draftBulto = newDraftBulto(this.useMetric);
      this.bultoError = "";
    },
    bultoPayload(d, status) {
      return {
        type: d.type,
        status,
        contents: d.contents.map(c => ({
          itemId: c.itemId,
          sku: c.sku,
          description: c.description,
          unit: c.unit,
          qty: +c.qty
        })),
        weight_display: { val: +d.weightVal || 0, unit: d.weightUnit },
        dims_display: {
          l: +d.l || 0,
          w: +d.w || 0,
          h: +d.h || 0,
          unit: d.dimUnit
        }
      };
    },
    // Autoguarda el bulto que se está armando como borrador ('open') apenas tiene items,
    // así no se pierde si se navega fuera de la página antes de cerrarlo.
    scheduleAutosave() {
      if (this.suppressAutosave) return;
      if (this.editingBultoId) return;
      if (!this.draftBulto.contents.length && !this.draftBulto.id) return;
      clearTimeout(this.draftSaveTimer);
      this.draftSaving = true;
      this.draftSaveTimer = setTimeout(() => this.autosaveDraft(), 700);
    },
    async autosaveDraft() {
      const d = this.draftBulto;
      // Guarda contra respuestas que llegan fuera de orden: si mientras esta llamada estaba
      // en vuelo se disparó otro autoguardado más nuevo (p.ej. se quitó un item y luego se
      // tipeó el peso), solo la respuesta de la ÚLTIMA llamada debe aplicarse; si no, una
      // respuesta vieja puede "revivir" un item que el usuario ya había quitado.
      const seq = ++this.autosaveSeq;
      try {
        if (!d.contents.length) {
          if (d.id) {
            const res = await deleteManifestBulto(this.orderNumber, d.id);
            if (seq === this.autosaveSeq) this.applyResponse(res);
          }
          d.id = null;
          return;
        }
        const payload = this.bultoPayload(d, "open");
        const res = d.id
          ? await updateManifestBulto(this.orderNumber, d.id, payload)
          : await createManifestBulto(this.orderNumber, payload);
        if (seq !== this.autosaveSeq) return;
        this.applyResponse(res);
        if (!d.id) {
          d.id = res.bultoId;
        }
      } catch (e) {
        // El autoguardado reintenta solo con el próximo cambio, pero el error se avisa una vez
        // para no dejar al usuario pensando que "no pasa nada" al agregar items.
        this.$toast.open({
          message:
            (e.response && e.response.data && e.response.data.message) ||
            "No se pudo guardar el borrador del bulto automáticamente.",
          type: "error",
          position: "top-right"
        });
        // El rechazo del servidor casi siempre es porque algún item del borrador ya no tiene
        // cantidad disponible (p.ej. quedó reservado en otro bulto). Si no se corrige el
        // borrador local, cada cambio vuelve a reintentar el mismo guardado inválido en
        // bucle. Se refresca la disponibilidad real desde el servidor (this.items puede estar
        // desactualizado) y se recorta el borrador contra ella para salir del estado inválido.
        if (seq === this.autosaveSeq) {
          try {
            const fresh = await getPackingManifest(this.orderNumber);
            if (seq === this.autosaveSeq) this.applyResponse(fresh);
          } catch (e2) {
            // si tampoco se puede refrescar, se reconcilia con el último estado conocido
          }
          this.reconcileDraftAgainstAvailability();
        }
      } finally {
        this.draftSaving = false;
      }
    },
    // Solo aplica cuando se hidrató un manifiesto con datos corruptos de antes de esta
    // corrección (más de un bulto 'open'): borra los duplicados sobrantes en el servidor
    // para dejar un único borrador antes de guardar la versión fusionada.
    async cleanupExtraOpenBultos(ids) {
      if (!ids || !ids.length) return;
      for (const id of ids) {
        try {
          const res = await deleteManifestBulto(this.orderNumber, id);
          this.applyResponse(res);
        } catch (e) {
          // se reintentará en el próximo load() si sigue quedando duplicado
        }
      }
    },
    reconcileDraftAgainstAvailability() {
      const d = this.draftBulto;
      d.contents = d.contents
        .map(c => {
          const item = this.items.find(i => i.id === c.itemId);
          const cap = item ? item.remainingQty : 0;
          return { ...c, max: cap, qty: this.clamp(c.qty, 0, cap) };
        })
        .filter(c => c.qty > 0);
    },
    async discardDraft() {
      if (!confirm(this.$t("message.order.confirm_discard_draft"))) return;
      try {
        if (this.draftBulto.id) {
          const res = await deleteManifestBulto(
            this.orderNumber,
            this.draftBulto.id
          );
          this.applyResponse(res);
        }
      } catch (e) {
        this.$toast.open({
          message: "No se pudo descartar el borrador.",
          type: "error",
          position: "top-right"
        });
        return;
      }
      this.draftBulto = newDraftBulto(this.useMetric);
    },
    async saveBulto() {
      const d = this.draftBulto;
      this.bultoError = "";
      // No mutamos el borrador (dispararía el autoguardado): las líneas en 0 sólo se
      // quedan fuera del bulto cerrado.
      if (!d.contents.some(c => +c.qty > 0)) {
        this.bultoError = this.$t("message.order.bulto_needs_item");
        return;
      }
      if (!(parseFloat(d.weightVal) > 0)) {
        this.bultoError = this.$t("message.order.bulto_needs_weight");
        return;
      }
      if (
        !(parseFloat(d.l) > 0 && parseFloat(d.w) > 0 && parseFloat(d.h) > 0)
      ) {
        this.bultoError = this.$t("message.order.bulto_needs_dims");
        return;
      }
      clearTimeout(this.draftSaveTimer);
      const payload = this.bultoPayload(d, "closed");
      payload.contents = payload.contents.filter(c => c.qty > 0);
      const targetId = this.editingBultoId || d.id;
      try {
        const res = targetId
          ? await updateManifestBulto(this.orderNumber, targetId, payload)
          : await createManifestBulto(this.orderNumber, payload);
        this.applyResponse(res);
        this.cancelEditBulto();
      } catch (e) {
        this.bultoError =
          (e.response && e.response.data && e.response.data.message) ||
          "No se pudo guardar el bulto.";
      }
    },
    async removeBulto(bulto) {
      if (!confirm(this.$t("message.order.confirm_delete_bulto"))) return;
      try {
        const res = await deleteManifestBulto(this.orderNumber, bulto.id);
        this.applyResponse(res);
      } catch (e) {
        this.$toast.open({
          message: "No se pudo eliminar el bulto.",
          type: "error",
          position: "top-right"
        });
      }
    },
    saveDispatchDebounced() {
      clearTimeout(this.dispatchSaveTimer);
      this.dispatchSaveTimer = setTimeout(async () => {
        try {
          const res = await updateManifestDispatchInfo(this.orderNumber, {
            picked_by: this.dispatch.pickedBy,
            revised_by: this.dispatch.revisedBy,
            shipped_by: this.dispatch.shippedBy
          });
          this.applyResponse(res);
        } catch (e) {
          // silent autosave failure, user can retry by typing again
        }
      }, 500);
    },
    async handleClose() {
      try {
        const res = await closeManifest(this.orderNumber);
        this.applyResponse(res);
        this.$toast.open({
          message: this.$t("message.order.dispatched"),
          type: "success",
          position: "top-right"
        });
      } catch (e) {
        this.$toast.open({
          message: "No se pudo marcar como despachada.",
          type: "error",
          position: "top-right"
        });
      }
    },
    async linkOrder() {
      const number = this.linkOrderNumber;
      if (!number || this.linking) return;
      if (!confirm(this.$t("message.order.confirm_link_order", { number })))
        return;
      this.linking = true;
      try {
        const res = await linkManifestOrder(this.orderNumber, number);
        this.applyResponse(res);
        // La otra orden pudo traer su propio bulto a medio armar.
        if (!this.draftBulto.contents.length && !this.editingBultoId) {
          await this.hydrateOpenDraft(res);
        }
        this.linkOrderNumber = "";
        this.$toast.open({
          message: this.$t("message.order.order_linked", { number }),
          type: "success",
          position: "top-right"
        });
      } catch (e) {
        this.$toast.open({
          message:
            (e.response && e.response.data && e.response.data.message) ||
            "No se pudo agregar la orden.",
          type: "error",
          position: "top-right"
        });
      } finally {
        this.linking = false;
      }
    },
    async unlinkOrder(order) {
      const number = order.orderNumber;
      if (!confirm(this.$t("message.order.confirm_unlink_order", { number })))
        return;
      try {
        const res = await unlinkManifestOrder(this.orderNumber, number);
        this.applyResponse(res);
      } catch (e) {
        this.$toast.open({
          message:
            (e.response && e.response.data && e.response.data.message) ||
            "No se pudo quitar la orden.",
          type: "error",
          position: "top-right"
        });
      }
    },
    async handleReopen() {
      try {
        const res = await reopenManifest(this.orderNumber);
        this.applyResponse(res);
      } catch (e) {
        this.$toast.open({
          message: "No se pudo reabrir el packing list.",
          type: "error",
          position: "top-right"
        });
      }
    }
  }
};
</script>

<style scoped>
.pm-thumb {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  padding: 0;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  background: #fff;
}
.pm-thumb:hover {
  border-color: #f2a93b;
}
.pm-thumb-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: #6b7280;
}
.pm-thumb-none {
  color: #d1d5db;
  border-style: dashed;
}
.pm-thumb-none:hover {
  border-color: #e5e7eb;
}
.pm-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.pm-photo-overlay {
  position: fixed;
  inset: 0;
  z-index: 300;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(17, 24, 39, 0.6);
  outline: none;
}
.pm-photo-dialog {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 720px;
  max-height: 100%;
  overflow: hidden;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
}
.pm-photo-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 12px 12px 16px;
  border-bottom: 1px solid #e5e7eb;
}
.pm-photo-title {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  line-height: 1.3;
  color: #6b7280;
}
.pm-photo-title b {
  color: #111827;
}
.pm-photo-close {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  font-size: 22px;
  color: #6b7280;
}
.pm-photo-close:hover {
  background: #f3f4f6;
  color: #111827;
}
.pm-photo-img {
  min-height: 0;
  width: 100%;
  max-height: calc(100vh - 140px);
  padding: 16px;
  object-fit: contain;
}
.tab-btn {
  padding: 0.55em 1.3em;
  border: 1px solid #d1d5db;
  border-radius: 999px;
  font-size: 0.9rem;
  text-transform: uppercase;
  background: #f9fafb;
  cursor: pointer;
}
.tab-btn.active {
  background: #111827;
  color: #fff;
  border-color: #111827;
}
.pack-progress-bar {
  height: 8px;
  border-radius: 4px;
  background: #e5e7eb;
  overflow: hidden;
}
.pack-progress-bar > div {
  height: 100%;
  background: #16a34a;
  transition: width 0.3s;
}
/* El formulario queda a la vista mientras se recorre la lista de items. */
@media (min-width: 1024px) {
  .pack-side {
    position: sticky;
    top: 1rem;
    align-self: start;
    max-height: calc(100vh - 2rem);
    overflow-y: auto;
  }
}
.draft-contents {
  max-height: 320px;
  overflow-y: auto;
}
.bulto-editing {
  border-color: #f2a93b;
  background: rgba(242, 169, 59, 0.08);
}
.unit-tag {
  display: inline-flex;
  align-items: center;
  padding: 0 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.25rem;
  background: #f3f4f6;
  color: #4b5563;
  font-size: 0.875rem;
  text-transform: uppercase;
}
.pill {
  padding: 0.4em 1.1em;
  border-radius: 999px;
  border: 1px solid #d1d5db;
  font-size: 0.85rem;
  cursor: pointer;
  text-transform: uppercase;
}
.pill.active {
  background: #111827;
  color: #fff;
  border-color: #111827;
}
.btn-sm {
  padding: 0.45em 1em;
  font-size: 0.88rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  white-space: nowrap;
}
.btn-primary-sm {
  padding: 0.55em 1.3em;
  font-size: 0.92rem;
  border-radius: 6px;
  background: #111827;
  color: #fff;
  border: none;
  cursor: pointer;
  white-space: nowrap;
}
.pm-orders {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.pm-order-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 10px;
  border: 1px solid #d1d5db;
  border-radius: 999px;
  background: #fff;
}
.pm-order-chip.principal {
  border-color: #3b82f6;
}
.pm-order-chip small {
  color: #6b7280;
}
.pm-order-chip button {
  color: #ef4444;
}
.pm-order-tag {
  font-size: 0.75rem;
  color: #6b7280;
}
.pl-order-ref {
  font-size: 0.85em;
  color: #6b7280;
}
.banner-warn {
  background: rgba(242, 169, 59, 0.12);
  border: 1px solid #f2a93b;
  color: #92600f;
  padding: 0.6em 1em;
  border-radius: 6px;
  font-size: 0.85rem;
}
/* Vista resumen/impresión: formato "Packing List" oficial (ver PDF de referencia) */
.pl-sheet {
  color: #000;
  padding: 1em 0;
  margin-top: 1em;
  font-size: 0.85rem;
}
.pl-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1em;
}
.pl-company {
  display: flex;
  align-items: flex-start;
  gap: 0.8em;
}
.pl-logo {
  width: 48px;
  height: 48px;
  object-fit: contain;
  flex: none;
}
.pl-company-name {
  font-weight: 800;
  font-size: 0.95rem;
}
.pl-company-line {
  font-size: 0.78rem;
  color: #333;
}
.pl-order-meta {
  text-align: right;
  font-size: 0.82rem;
  white-space: nowrap;
}
.pl-title {
  text-align: center;
  font-size: 1.6rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  margin: 0.6em 0 1em;
}
.pl-address-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  margin-bottom: 1em;
}
.pl-address-box {
  border: 1px solid #000;
  padding: 0.6em 0.8em;
  font-size: 0.82rem;
}
.pl-address-row .pl-address-box + .pl-address-box {
  border-left: none;
}
.pl-address-label {
  font-weight: 800;
  margin-bottom: 0.2em;
}
.pl-table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 0.68rem;
  margin-bottom: 1em;
}
.pl-table th {
  text-align: left;
  font-size: 0.62rem;
  font-weight: 700;
  border: 1px solid #000;
  padding: 0.25em 0.4em;
  background: #f2f2f2;
}
.pl-table td {
  border: 1px solid #999;
  padding: 0.2em 0.4em;
  vertical-align: middle;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* La primera columna (tipo/número de bulto) y las etiquetas de totales van en una línea. */
.pl-table th:first-child,
.pl-tot-label {
  white-space: nowrap;
}
.pl-subtotal-row td {
  font-weight: 700;
  border-top: 1px solid #000;
  border-bottom: 1px solid #000;
}
.pl-totals-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
  margin-bottom: 1.6em;
}
.pl-totals-table td {
  border: 1px solid #000;
  padding: 0.5em 0.8em;
}
.pl-tot-label {
  font-weight: 700;
  width: 22%;
}
.pl-tot-value {
  width: 28%;
}
.pl-signlines {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 1.5em;
}
.pl-sl label {
  display: block;
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #6b6552;
  margin-bottom: 1.4em;
}
.pl-sl input {
  width: 100%;
  border: none;
  border-bottom: 1px solid #000;
  background: transparent;
  padding: 0.2em 0;
  font-size: 0.95rem;
  color: #000;
}
.pl-sl input:focus {
  outline: none;
  border-bottom: 2px solid #000;
}
@media (max-width: 700px) {
  .pl-address-row,
  .pl-signlines {
    grid-template-columns: 1fr;
  }
  .pl-address-row .pl-address-box + .pl-address-box {
    border-left: 1px solid #000;
    border-top: none;
  }
}
/* Recibo de entrega: reutiliza la hoja .pl-* y agrega estas piezas */
.rc-info-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  margin-bottom: 1em;
}
.rc-info-row .pl-address-box + .pl-address-box {
  border-left: none;
}
.rc-table td {
  font-size: 0.8rem;
  padding: 0.35em 0.4em;
}
.rc-ack {
  font-size: 0.8rem;
  font-style: italic;
  margin-bottom: 1.6em;
}
.rc-signs {
  display: grid;
  grid-template-columns: 1fr 1.4fr;
  gap: 3em;
}
.rc-sign-title {
  font-weight: 800;
  text-transform: uppercase;
  font-size: 0.8rem;
  margin-bottom: 0.4em;
}
.rc-sign-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5em;
}
.rc-sign-line {
  margin-top: 1.6em;
}
.rc-sign-line span {
  display: block;
  min-height: 1.4em;
  border-bottom: 1px solid #000;
  font-size: 0.95rem;
}
.rc-sign-line label {
  display: block;
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #6b6552;
  margin-top: 0.2em;
}
@media (max-width: 700px) {
  .rc-info-row,
  .rc-signs {
    grid-template-columns: 1fr;
  }
  .rc-info-row .pl-address-box + .pl-address-box {
    border-left: 1px solid #000;
    border-top: none;
  }
}
.btn-row {
  display: flex;
  gap: 0.6em;
}
.draft-badge {
  display: inline-block;
  margin-left: 0.4em;
  padding: 0.1em 0.5em;
  border-radius: 999px;
  background: rgba(180, 83, 9, 0.12);
  color: #b45309;
  font-size: 0.62rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
}

@media print {
  .pl-sheet {
    padding: 0;
  }
  .pl-table th {
    background: #eee !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
}
</style>

<!--
  Sin "scoped": las reglas de impresión deben alcanzar el chrome de la app (sidebar/header de
  LayoutLogged), que vive fuera del árbol de este componente. Un <style scoped> nunca podría
  ocultarlos porque Vue les agregaría un atributo de scope distinto al del layout real.
-->
<style>
@media print {
  /* Otros componentes globales (AlertModal) inyectan sin
     scope "body * { visibility: hidden }" + "#print-area { visibility: visible }" para SU
     propio flujo de impresión. Como sus <style> quedan en el <head> para toda la sesión de
     la SPA (no solo mientras esos componentes están montados), esa regla oculta esta página
     entera aunque no tenga nada que ver con ellos. Se revierte explícitamente acá: al no
     existir ningún #print-area en esta página, forzamos todo visible de nuevo. */
  body,
  body * {
    visibility: visible !important;
  }

  #left-side-bar,
  #header,
  .no-print {
    display: none !important;
  }
  /* .container-custom (App.scss) tiene overflow:hidden para el scroll interno de la SPA;
     el motor de impresión no pagina contenido recortado por overflow, así que queda en
     blanco. Hay que dejarlo fluir libremente solo durante la impresión. */
  html,
  body,
  .LayoutLogged,
  .container-custom,
  .LayoutLogged__main,
  .main__content {
    overflow: visible !important;
    height: auto !important;
    min-height: 0 !important;
    background: #fff !important;
  }
  .LayoutLogged .container-custom {
    margin-left: 0 !important;
  }
  .print-container {
    max-width: 100% !important;
    margin: 0 !important;
    padding: 0.4in 0.5in !important;
  }
}
</style>
